import React, { useRef } from "react";
import styles from "./dashboard-timeseries-info-with-popover.module.scss";
import { useI18N } from "@src/language/language-context";
import { NodeIdBlock } from "../node-details-panel/node-id-block/node-id-block";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import questionMarkIcon from "@src/assets/png/question-mark-circle.png";
import { useDashboardTimeseriesPopover } from "./use-dashboard-timeseries-info-with-popover-hook";

type NodeInfo = {
  nodeName: string;
  tsid: string;
  nodeId: string;
};

type DashboardTimeseriesInfoWithPopoverProps = {
  nodeInfoList: NodeInfo[];
};

export const DashboardTimeseriesInfoWithPopover = ({
  nodeInfoList,
}: DashboardTimeseriesInfoWithPopoverProps) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const i18n = useI18N();

  const texts = {
    successMessage: i18n(
      "modules.dashboardTimeseriesInfoWithPopover.copiedToClipboardSnackBarMessage",
    ),
    errorMessage: i18n(
      "modules.dashboardTimeseriesInfoWithPopover.nodeSnackBarError",
    ),
    textLabel: i18n("modules.dashboardTimeseriesInfoWithPopover.textLabel"),
    nodeName: i18n("modules.dashboardTimeseriesInfoWithPopover.nodeName"),
    tsId: i18n("modules.dashboardTimeseriesInfoWithPopover.tsId"),
    nodeId: i18n("modules.dashboardTimeseriesInfoWithPopover.nodeId"),
    timeSeriesInfoLabel: i18n(
      "modules.dashboardTimeseriesInfoWithPopover.textLabel",
    ),
    noDataMessage: i18n(
      "modules.dashboardTimeseriesInfoWithPopover.noDataMessage",
    ),
  };
  const {
    isPopoverOpen,
    popoverStyle,
    handleClickOnInfo,
    copyNodeIdToClipBoard,
  } = useDashboardTimeseriesPopover(popoverRef, popoverContentRef, texts);

  return (
    <div ref={popoverRef}>
      <div className={styles["dashboard-timeseries"]}>
        <div className={styles["dashboard-timeseries__icon-wrapper"]}>
          <div className={styles["dashboard-timeseries__icon-button-wrapper"]}>
            <IconButton fun={handleClickOnInfo} icon={questionMarkIcon} />
          </div>
        </div>
      </div>
      {isPopoverOpen && (
        <div
          style={popoverStyle}
          className={styles["dashboard-timeseries__popover-wrapper"]}
          ref={popoverContentRef}
        >
          <div className={styles["dashboard-timeseries__popover"]}>
            <h3 className={styles["dashboard-timeseries__popover-title"]}>
              {texts.timeSeriesInfoLabel}
            </h3>
            <div className={styles["dashboard-timeseries__info"]}>
              {!Array.isArray(nodeInfoList) || nodeInfoList.length === 0 ? (
                <div className={styles["dashboard-timeseries__popover-nodata"]}>
                  {texts.noDataMessage}
                </div>
              ) : (
                nodeInfoList?.map((info, idx) => (
                  <div
                    className={
                      styles["dashboard-timeseries__popover-dialogue-multiple"]
                    }
                    key={info.nodeId || idx}
                  >
                    {info.nodeName && (
                      <NodeIdBlock
                        copyFunction={() =>
                          copyNodeIdToClipBoard(info.nodeName, texts.nodeName)
                        }
                        id={info.nodeName}
                        label={texts.nodeName}
                        tooltip={info.nodeName}
                        tooltipPosition="top"
                        copyTooltip={info.nodeName}
                        copyTooltipPosition="left"
                      />
                    )}
                    {info.tsid && (
                      <NodeIdBlock
                        copyFunction={() =>
                          copyNodeIdToClipBoard(info.tsid, texts.tsId)
                        }
                        id={info.tsid}
                        label={texts.tsId}
                        tooltip={info.tsid}
                        tooltipPosition="top"
                        copyTooltip={info.tsid}
                        copyTooltipPosition="left"
                      />
                    )}
                    {info.nodeId && (
                      <NodeIdBlock
                        copyFunction={() =>
                          copyNodeIdToClipBoard(info.nodeId, texts.nodeId)
                        }
                        id={info.nodeId}
                        label={texts.nodeId}
                        tooltip={info.nodeId}
                        tooltipPosition="top"
                        copyTooltip={info.nodeId}
                        copyTooltipPosition="left"
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
